import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Table from '../components/Table';
import './Salons.css';

function Salons() {
  const navigate = useNavigate();
  const [salons, setSalons] = useState([]);
  const [filteredSalons, setFilteredSalons] = useState([]);
  const [pendingApprovalSalons, setPendingApprovalSalons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [error, setError] = useState('');
  const [approvalFilter, setApprovalFilter] = useState('all');
  const [lightboxPhotos, setLightboxPhotos] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [brokenPhotoKeys, setBrokenPhotoKeys] = useState(() => new Set());

  const markPhotoBroken = (key) => {
    setBrokenPhotoKeys((prev) => new Set(prev).add(key));
  };


  const columns = [
    {
      key: 'salonName',
      title: 'Salon Name',
      className: 'salon-name-col'
    },
    {
      key: 'ownerName',
      title: 'Owner Name'
    },
    {
      key: 'contactNumber',
      title: 'Contact Number'
    },
    {
      key: 'address',
      title: 'Address',
      className: 'address-col'
    },
    {
      key: 'servicesFor',
      title: 'Services For',
      render: (value) => {
        if (Array.isArray(value)) {
          return value.join(', ');
        }
        return value || 'N/A';
      }
    },
    {
      key: 'serviceCount',
      title: 'Total Services',
      render: (value, row) => {
        return row.services ? row.services.length : 0;
      }
    },
    {
      key: 'status',
      title: 'Status',
      render: (value, row) => {
        const isActive = row.profileCompleted && !row.isBlocked;
        return (
          <span className={`status-badge ${isActive ? 'status-active' : 'status-inactive'}`}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        );
      }
    },
    {
      key: 'approvalStatus',
      title: 'Approval Status',
      render: (value, row) => {
        const approvalStatus = row.approvalStatus || 'pending';
        let statusClass = '';
        let statusText = '';
        
        switch (approvalStatus) {
          case 'pending':
            statusClass = 'approval-pending';
            statusText = 'Pending';
            break;
          case 'awaiting_approval':
            statusClass = 'approval-awaiting';
            statusText = 'Awaiting Approval';
            break;
          case 're_upload':
            statusClass = 'approval-reupload';
            statusText = 'Re-upload Requested';
            break;
          case 'approved':
            statusClass = 'approval-approved';
            statusText = 'Approved';
            break;
          case 'rejected':
            statusClass = 'approval-rejected';
            statusText = 'Rejected';
            break;
          default:
            statusClass = 'approval-pending';
            statusText = 'Pending';
        }
        
        return (
          <span className={`approval-status-badge ${statusClass}`}>
            {statusText}
          </span>
        );
      }
    },
    {
      key: 'createdAt',
      title: 'Joined Date',
      render: (value) => {
        if (value && value.toDate) {
          return value.toDate().toLocaleDateString();
        }
        return new Date(value).toLocaleDateString();
      }
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (value, row) => (
        <div className="salon-row-actions">
          <button
            className="payments-btn"
            onClick={() => navigate(`/payments/${row.salonId}`)}
          >
            Payments
          </button>
          <button
            className="services-btn"
            onClick={() => navigate(`/salons/${row.salonId}/services`)}
            title="Read-only — service changes go through Service Update Requests"
          >
            View Services
          </button>
        </div>
      )
    }
  ];

  useEffect(() => {
    fetchSalons();
    fetchPendingApprovalSalons();
  }, []);

  const filterSalons = useCallback(() => {
    if (approvalFilter === 'all') {
      setFilteredSalons(salons);
    } else {
      setFilteredSalons(salons.filter(salon => salon.approvalStatus === approvalFilter));
    }
  }, [salons, approvalFilter]);

  useEffect(() => {
    filterSalons();
  }, [filterSalons]);

  const handleFilterChange = (filterValue) => {
    setApprovalFilter(filterValue);
  };

  const fetchSalons = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch all salons from the salons collection
      const salonsQuery = collection(db, 'salons');
      
      const querySnapshot = await getDocs(salonsQuery);
      const salonsData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        salonsData.push({
          id: doc.id,
          salonId: doc.id, // salonId = owner's uid
          salonName: data.profile?.shopName || 'N/A',
          contactNumber: data.phoneNumber || 'N/A',
          ownerName: data.profile?.ownerName || 'N/A',
          address: data.profile?.address || 'N/A',
          servicesFor: data.profile?.servicesFor || [],
          services: data.profile?.services || [],
          createdAt: data.createdAt,
          profileCompleted: data.profileCompleted || false,
          approvalStatus: data.approvalStatus || 'pending',
          isBlocked: data.isBlocked || false,
          salonPhotos: data.salonPhotos || []
        });
      });
      
      // Sort by creation date (newest first)
      salonsData.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB - dateA;
      });
      
      setSalons(salonsData);
    } catch (err) {
      setError('Failed to load salons. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovalSalons = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch salons awaiting approval from the salons collection
      const pendingQuery = query(
        collection(db, 'salons'),
        where('approvalStatus', '==', 'awaiting_approval')
      );
      
      const querySnapshot = await getDocs(pendingQuery);
      const pendingData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();

        pendingData.push({
          id: doc.id,
          salonId: doc.id, // salonId = owner's uid
          salonName: data.profile?.shopName || 'N/A',
          contactNumber: data.phoneNumber || 'N/A',
          ownerName: data.profile?.ownerName || 'N/A',
          address: data.profile?.address || 'N/A',
          servicesFor: data.profile?.servicesFor || [],
          services: data.profile?.services || [],
          salonPhotos: data.salonPhotos || [],
          createdAt: data.createdAt,
          profileCompleted: data.profileCompleted || false,
          approvalStatus: data.approvalStatus,
          isBlocked: data.isBlocked || false
        });
      });
      
      // Sort by creation date (newest first)
      pendingData.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB - dateA;
      });
      
      setPendingApprovalSalons(pendingData);
    } catch (err) {
      setError('Failed to load pending approval salons. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalAction = async (salonId, action) => {
    try {
      setApprovalLoading(true);

      const salonRef = doc(db, 'salons', salonId);
      
      let updateData = {};
      let actionMessage = '';
      
      switch (action) {
        case 'approve':
          updateData = { approvalStatus: 'approved' };
          actionMessage = 'approved';
          break;
        case 'reject':
          updateData = { approvalStatus: 'rejected' };
          actionMessage = 'rejected';
          break;
        case 'ask_upload':
          updateData = { approvalStatus: 're_upload' };
          actionMessage = 'marked for re-upload';
          break;
        default:
          throw new Error('Invalid action');
      }
      
      await updateDoc(salonRef, updateData);
      console.log('✅ Successfully updated approval status');
      
      // Refresh both lists
      await fetchSalons();
      await fetchPendingApprovalSalons();
      
      // Show success message
      alert(`Salon ${actionMessage} successfully!`);
    } catch (err) {
      if (err.code === 'permission-denied') {
        alert('❌ Permission denied! Please check Firestore security rules for admin access.');
      } else {
        alert(`❌ Failed to update approval status: ${err.message}`);
      }
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchSalons();
    fetchPendingApprovalSalons();
  };

  if (error) {
    return (
      <div className="salons-page">
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button onClick={handleRefresh} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="salons-page">
      <div className="page-header">
        <div className="header-info">
          <h2>Salons Management</h2>
          <p>Total: {salons.length} registered salons | Showing: {filteredSalons.length} salons</p>
        </div>
        <div className="header-actions">
          <div className="filter-container">
            <label htmlFor="approval-filter">Filter by Approval Status:</label>
            <select 
              id="approval-filter"
              value={approvalFilter} 
              onChange={(e) => handleFilterChange(e.target.value)}
              className="approval-filter-select"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="awaiting_approval">Awaiting Approval</option>
              <option value="re_upload">Re-upload Requested</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <button onClick={handleRefresh} className="refresh-btn" disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="stats-container">
        <div className="stat-card">
          <h3>Active Salons</h3>
          <p className="stat-number">
            {salons.filter(salon => salon.profileCompleted && !salon.isBlocked).length}
          </p>
        </div>
        <div className="stat-card">
          <h3>Inactive Salons</h3>
          <p className="stat-number">
            {salons.filter(salon => !salon.profileCompleted || salon.isBlocked).length}
          </p>
        </div>
        <div className="stat-card">
          <h3>Approved Salons</h3>
          <p className="stat-number" style={{color: '#065f46'}}>
            {salons.filter(salon => salon.approvalStatus === 'approved').length}
          </p>
        </div>
        <div className="stat-card approval-stat">
          <h3>Awaiting Approval</h3>
          <p className="stat-number approval-count">
            {salons.filter(salon => salon.approvalStatus === 'awaiting_approval').length}
          </p>
        </div>
        <div className="stat-card">
          <h3>Rejected</h3>
          <p className="stat-number" style={{color: '#991b1b'}}>
            {salons.filter(salon => salon.approvalStatus === 'rejected').length}
          </p>
        </div>
      </div>

      {/* Approval Section */}
      {pendingApprovalSalons.length > 0 && (
        <div className="approval-section">
          <div className="section-header">
            <h3>Waiting for Approval ({pendingApprovalSalons.length})</h3>
            <p>Review salon photos and approve or reject applications</p>
          </div>
          
          <div className="approval-cards-container">
            {pendingApprovalSalons.map((salon) => (
              <div key={salon.id} className="approval-card">
                <div className="salon-info">
                  <h4>{salon.salonName}</h4>
                  <p><strong>Owner:</strong> {salon.ownerName}</p>
                  <p><strong>Contact:</strong> {salon.contactNumber}</p>
                  <p><strong>Address:</strong> {salon.address}</p>
                  <p><strong>Services For:</strong> {Array.isArray(salon.servicesFor) ? salon.servicesFor.join(', ') : salon.servicesFor}</p>
                </div>
                
                <div className="salon-photos">
                  <h5>Uploaded Photos: ({salon.salonPhotos ? salon.salonPhotos.length : 0} photos)</h5>
                  {salon.salonPhotos && salon.salonPhotos.length > 0 ? (
                    <div className="photos-grid">
                      {salon.salonPhotos.map((photoObj, index) => {
                        const imageUrl = photoObj.url || photoObj;
                        const photoKey = `${salon.id}-${index}`;

                        return (
                          <div key={index} className="photo-item">
                            {brokenPhotoKeys.has(photoKey) ? (
                              <div className="photo-error">Image unavailable</div>
                            ) : (
                              <img
                                src={imageUrl}
                                alt={`${salon.salonName} ${index + 1}`}
                                className="salon-photo"
                                loading="lazy"
                                onClick={() => {
                                  setLightboxPhotos(salon.salonPhotos);
                                  setLightboxIndex(index);
                                }}
                                onError={() => markPhotoBroken(photoKey)}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="no-photos">No photos uploaded (Array length: {salon.salonPhotos ? salon.salonPhotos.length : 'undefined'})</p>
                  )}
                </div>
                
                <div className="approval-actions">
                  <button 
                    className="approve-btn"
                    onClick={() => handleApprovalAction(salon.id, 'approve')}
                    disabled={approvalLoading}
                  >
                    Approve
                  </button>
                  <button 
                    className="ask-upload-btn"
                    onClick={() => handleApprovalAction(salon.id, 'ask_upload')}
                    disabled={approvalLoading}
                  >
                    Ask Upload Again
                  </button>
                  {/* <button 
                    className="reject-btn"
                    onClick={() => handleApprovalAction(salon.id, 'reject')}
                    disabled={approvalLoading}
                  >
                    Reject
                  </button> */}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="table-section">
        <Table
          columns={columns}
          data={filteredSalons}
          loading={loading}
          emptyMessage="No salons found"
        />
      </div>

      {lightboxPhotos && lightboxPhotos[lightboxIndex] && (
        <div className="photo-lightbox-overlay" onClick={() => setLightboxPhotos(null)}>
          <button
            className="photo-lightbox-close"
            onClick={() => setLightboxPhotos(null)}
            aria-label="Close"
          >
            ×
          </button>
          <img
            src={lightboxPhotos[lightboxIndex].url || lightboxPhotos[lightboxIndex]}
            alt={`Salon ${lightboxIndex + 1}`}
            className="photo-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default Salons;


